import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Helpers from 'utils/Helpers';
import { Input, Form, theme, notification } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { globalOp } from 'store/ducks/Global';
import AuthLayout from 'components/Layouts/AuthLayout';
import { RootState } from 'store/configureStore';
import * as Styled from './Login.styled';

const { useToken } = theme;

const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useToken();
  const [form] = Form.useForm();
  const { isSuperAdmin } = useSelector((state: RootState) => state.global);

  useEffect(() => {
    const userToken = Helpers.getToken();
    if (userToken && !Helpers.isObjEmpty(userToken)) {
      if (isSuperAdmin) {
        navigate('/super_admin', { replace: true });
      } else {
        navigate('/working_area', { replace: true });
      }
    }
  }, [navigate, isSuperAdmin]);

  const handleSuperAdminLogin = async () => {
    try {
      const values = await form.validateFields();

      const response = await globalOp.superAdminLogin(
        values.email,
        values.password
      );

      notification.info({
        message: response.data.message,
        placement: 'topRight',
        duration: 20
      });
    } catch (error: any) {
      notification.error({
        message: 'Info',
        description:
          error?.response?.data?.error ||
          'An error occurred. Please try again.',
        placement: 'topRight',
        duration: 10
      });
    }
  };

  const [emailLoading, setEmailLoading] = useState<boolean>(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleEmailLogin = async () => {
    if (!email || !password) {
      notification.error({
        message: 'Required Fields',
        description: 'Please enter your email and password!',
        duration: 20
      });
      return;
    }

    setEmailLoading(true);
    globalOp
      .requestLoginEmail(email, password)
      .then(() => {
        setEmailLoading(false);
      })
      .catch(() => {
        setEmailLoading(false);
      });
  };

  return (
    <AuthLayout>
      <Form form={form} name='login'>
        <Styled.ItemsContainer>
          <Styled.LoginFormItem
            token={token}
            label='Username/Email'
            name='email'
            rules={[
              {
                required: true,
                message: 'Please input your email!'
              }
            ]}
          >
            <Input
              prefix={<UserOutlined className='site-form-item-icon' />}
              type='email'
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                form.setFieldsValue({ email: event.target.value });
              }}
            />
          </Styled.LoginFormItem>
          <Styled.LoginFormItem
            token={token}
            label='Password'
            name='password'
            rules={[
              {
                required: true,
                message: 'Please input your password!'
              }
            ]}
          >
            <Input.Password
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                form.setFieldsValue({ password: event.target.value });
              }}
            />
          </Styled.LoginFormItem>
        </Styled.ItemsContainer>

        <Styled.BottomSection>
          <Styled.StyledButton
            token={token}
            type='primary'
            onClick={handleEmailLogin}
            loading={emailLoading}
          >
            Login
          </Styled.StyledButton>
          <Styled.StyledButton
            token={token}
            type='primary'
            onClick={handleSuperAdminLogin}
          >
            Log in as a super admin
          </Styled.StyledButton>
          <Styled.StyledLink to='/forgot_pass'>
            <span>Forgot your password?</span>
            <span>Click here</span>
          </Styled.StyledLink>
          <Styled.StyledDisabledButton disabled>
            Accept Cookies
          </Styled.StyledDisabledButton>
        </Styled.BottomSection>
      </Form>
    </AuthLayout>
  );
};

export default LoginForm;
